import CircularProgress from '@mui/material/CircularProgress'
import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'

import DomainObjectForm from 'app/shared-components/DomainObjectForm'
import {
    FormPart,
} from 'app/types/form.types'
import RichTable from 'app/shared-components/RichTableReduxWrapper'
import Button from 'app/shared-components/Button'
import HistoryBlockPrompt from 'app/shared-components/HistoryBlockPrompt'
import useBeforeUnloadDialog from 'app/hooks/useBeforeUnloadDialog'
import {
    RichTableColumns,
} from 'app/types/richtable.types'
import WarningCard from 'app/shared-components/WarningCard'
import {
    WarningCardVariant,
} from 'app/enums/common.enums'
import SkyNetPagination from 'app/shared-components/SkyNetPagination'
import {
    FailedUpload,
    FeedbackFormData,
} from 'app/shared-components/SimpleBulkUpload/SimpleBulkUpload.types'
import useSharedStyles from 'app/shared-components/SimpleBulkUpload/SimpleBulkUpload.styles'

import useStyles from './UploadFeedback.styles'
import usePaginatedData from './hooks/usePaginatedData'

type Props = {
    failedUploads: FailedUpload[],
    isLoading: boolean
    uniqField: string
    feedbackFormValue: FeedbackFormData
    formFields: FormPart<keyof FeedbackFormData>[]
    onStopUpdate: () => void
    onDoneClick: () => void
    columns?: RichTableColumns<any>[]
    rowsPerPage?: number
}

const UploadFeedback = ({
    failedUploads,
    isLoading,
    uniqField,
    formFields,
    feedbackFormValue,
    onStopUpdate,
    onDoneClick,
    columns,
    rowsPerPage = 25,
}: Readonly<Props>) => {
    const {
        classes,
    } = useStyles()
    const {
        classes: sharedClasses,
    } = useSharedStyles()

    useBeforeUnloadDialog(isLoading)

    const [
        stopped,
        setStopped,
    ] = useState({
        isStopped: false,
        stoppedManually: false,
    })

    useEffect(() => {
        if (!isLoading && !stopped.isStopped) {
            setStopped({
                stoppedManually: false,
                isStopped: true,
            })
        }
    }, [
        isLoading,
        stopped.isStopped,
    ])

    const {
        currentPageData,
        totalPages,
        currentPage,
        setCurrentPage,
        totalRecords,
    } = usePaginatedData<FailedUpload>({
        data: failedUploads,
        rowsPerPage,
    })

    const load = useCallback(() => {
        return Promise.resolve({
            data: currentPageData,
        })
    }, [currentPageData])

    const handleStopUpdate = useCallback(() => {
        setStopped({
            isStopped: true,
            stoppedManually: true,
        })
        onStopUpdate()
    }, [onStopUpdate])

    const FinalMessage = useMemo(() => {
        if (stopped.isStopped && stopped.stoppedManually) {
            return (
                <WarningCard variant={WarningCardVariant.Warning}>
                    <span className={classes.bold}>IMPORTANT: </span>
                    The update was stopped by user.
                </WarningCard>
            )
        }
        return (
            <div>
                <WarningCard variant={WarningCardVariant.Success}>
                    <span className={classes.bold}>IMPORTANT: </span>
                    The update is successfully completed.
                </WarningCard>
                <div className={classes.textMessage}>
                    All selected records were processed. You can review the outcome below:
                </div>
            </div>

        )
    }, [
        classes.bold,
        classes.textMessage,
        stopped.isStopped,
        stopped.stoppedManually,
    ])

    return (
        <>
            <div className={classes.container}>
                {isLoading ? (
                    <div className={sharedClasses.loadingContainer}>
                        <CircularProgress />
                        Update in progress...
                    </div>
                ) : FinalMessage}

                <DomainObjectForm
                    value={feedbackFormValue}
                    fields={formFields}
                    wrapper="noCard"
                    classNames={{
                        gridCardWrapper: classes.gridCardWrapper,
                    }}
                />

                {failedUploads.length > 0 && (
                    <>
                        <RichTable
                            uniqField={uniqField}
                            configName="UploadFeedbackTable"
                            name="UploadFeedback"
                            load={load}
                            columns={columns}
                            className={classes.tableContainer}
                            classNames={{
                                contentWrapper: classes.tableContentWrapper,
                            }}
                        />
                        {totalPages > 1 && (
                            <div className={classes.paginationContainer}>
                                <SkyNetPagination
                                    page={currentPage}
                                    total={totalRecords}
                                    rowsPerPage={rowsPerPage}
                                    onChange={(_, page) => {
                                        setCurrentPage(page)
                                    }}
                                    useCard={false}
                                />
                            </div>
                        )}
                    </>

                )}

                {(!stopped.isStopped) ? (
                    <Button
                        label="Stop the update"
                        onClick={handleStopUpdate}
                        className={classes.button}
                        alert
                    />
                ) : (
                    <Button
                        label="Done"
                        onClick={onDoneClick}
                        className={classes.button}
                    />
                )}
            </div>

            <HistoryBlockPrompt
                dialogTitle="Upload still in progress"
                promptText="You are about to leave this page, upload progress will be stopped. Would you like to discard anyway?"
                positiveLabel="Discard"
                when={isLoading}
                action={handleStopUpdate}
                isPositiveAlert
            />
        </>
    )
}

export default UploadFeedback
